import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { AccessDenied } from '../../components/access-denied';
import { ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { toast } from '../../lib/toast';
import { approveDocument, fetchDocuments, getDocumentViewUrl, rejectDocument } from './api';
import {
  approveErrorMessage,
  kycVerdictNotApplied,
  kycVerdictNotAppliedMessage,
} from './approve-error';
import {
  DecisionButtons,
  DocumentCard,
  DocumentChecks,
  DocumentPreview,
} from './document-card';
import {
  StatusChip,
  documentFile,
  documentWhen,
  documentWho,
  statusTone,
} from './document-status';
import { FilterTabs, type FilterTab } from './filter-tabs';
import { MIN_REJECT_REASON_LENGTH, RejectModal } from './reject-modal';
import { DocumentVerificationStatus, type DocumentWithUser, type DocumentsFilter } from './types';

/** Tab order, as the artboard lays it out left to right. */
const TABS: DocumentsFilter[] = ['pending', 'verified', 'rejected', 'all'];

/**
 * KYC document verification — `Redesign-D-Admin{,-Mobile}`, screen «Документи».
 *
 * The artboard's argument is that verification is a two-pane job: the queue on
 * the left, the document under judgement on the right, so the verdict is given
 * without leaving the list. The phone cannot hold two panes, so the same panel
 * folds into the row it belongs to (see {@link DocumentCard}).
 *
 * The screen is usually EMPTY — a healthy KYC queue has nothing in it — which
 * is why the empty state is drawn on the artboard rather than left to chance.
 */
export function DocumentsPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<DocumentsFilter>('pending');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [rejectTarget, setRejectTarget] = useState<DocumentWithUser | null>(null);

  const documentsQuery = useQuery({
    queryKey: ['admin-documents'],
    // Wrapped, not passed by reference: react-query hands its own context as
    // the first argument, which the optional status filter would swallow.
    queryFn: () => fetchDocuments(),
  });

  const refetchDocuments = () =>
    queryClient.invalidateQueries({ queryKey: ['admin-documents'] });

  const approveMutation = useMutation({
    mutationFn: approveDocument,
    onSuccess: () => {
      toast.success(t('verification.admin.docs.approveSuccess'));
      void refetchDocuments();
    },
    // Same protocol as the client detail screen's ladder card: a 409
    // `KYC_VERDICT_NOT_APPLIED` means the document IS verified and only the
    // client's promotion failed, so the queue has to be re-read — otherwise the
    // row stays in «На розгляді» over a decision the server already took.
    onError: (error) => {
      const notPromoted = kycVerdictNotApplied(error);
      toast.error(
        notPromoted
          ? kycVerdictNotAppliedMessage(
              notPromoted,
              t('verification.admin.docs.approveNotPromoted'),
            )
          : approveErrorMessage(error, t('verification.admin.docs.approveError')),
      );
      if (notPromoted) void refetchDocuments();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ documentId, reason }: { documentId: string; reason: string }) =>
      rejectDocument(documentId, reason),
    onSuccess: () => {
      toast.success(t('verification.admin.docs.rejectSuccess'));
      setRejectTarget(null);
      setComment('');
      void refetchDocuments();
    },
    onError: () => toast.error(t('verification.admin.docs.rejectError')),
  });

  const handleView = async (documentId: string) => {
    try {
      const data = await getDocumentViewUrl(documentId);
      window.open(data.presignedUrl, '_blank');
    } catch {
      toast.error(t('verification.admin.docs.viewError'));
    }
  };

  const error = documentsQuery.error;
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return <AccessDenied />;
  }

  const documents = documentsQuery.data?.documents ?? [];
  const statusOf = (doc: DocumentWithUser) =>
    doc.verificationStatus ?? DocumentVerificationStatus.PENDING;
  const countOf = (key: DocumentsFilter) =>
    key === 'all' ? documents.length : documents.filter((doc) => statusOf(doc) === key).length;
  const tabs: FilterTab[] = TABS.map((key) => ({ key, count: countOf(key) }));

  const filtered = documents.filter((doc) => filter === 'all' || statusOf(doc) === filter);

  // The panel always has something in it while the list does: an operator who
  // switches tabs should land on the first document of the new tab, not on an
  // empty pane holding the selection from the tab they left.
  const selected = filtered.find((doc) => doc._id === selectedId) ?? filtered[0];
  const isBusy = approveMutation.isPending || rejectMutation.isPending;

  // `comment` is deliberately NOT cleared here: on desktop the moderator has
  // already written the note in the panel's textarea, and the dialog is the
  // confirmation of that note rather than a second place to type it.
  const openRejectFor = (doc: DocumentWithUser) => setRejectTarget(doc);

  const confirmReject = () => {
    const reason = comment.trim();
    if (!rejectTarget?._id || reason.length < MIN_REJECT_REASON_LENGTH) return;
    rejectMutation.mutate({ documentId: rejectTarget._id, reason });
  };

  return (
    <>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
          {t('verification.admin.docs.title')}
        </h1>
        <p className="mt-1.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:mt-2 lg:text-[11px] lg:tracking-[0.14em]">
          {t('verification.admin.docs.subtitle', { count: String(countOf('pending')) })}
        </p>
      </div>

      <FilterTabs filter={filter} tabs={tabs} onChange={setFilter} />

      {documentsQuery.isPending ? (
        <Notice text={t('verification.admin.docs.loading')} />
      ) : documentsQuery.isError ? (
        <Notice text={t('verification.admin.docs.failed')} />
      ) : filtered.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* ── Mobile: accordion cards ─────────────────────────────────── */}
          <div className="flex flex-col gap-3 lg:hidden">
            {filtered.map((doc) => (
              <DocumentCard
                key={doc._id}
                document={doc}
                open={openId === doc._id}
                onToggle={() => setOpenId(openId === doc._id ? null : (doc._id ?? null))}
                onView={handleView}
                onApprove={(documentId) => approveMutation.mutate(documentId)}
                onReject={openRejectFor}
                isBusy={isBusy}
              />
            ))}
          </div>

          {/* ── Desktop: queue beside the document under judgement ──────── */}
          <div className="hidden flex-wrap items-start gap-5 lg:flex">
            <section className="min-w-0 flex-1 basis-[420px] overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface">
              {filtered.map((doc) => (
                <QueueRow
                  key={doc._id}
                  document={doc}
                  active={doc._id === selected?._id}
                  onPick={() => setSelectedId(doc._id ?? null)}
                />
              ))}
            </section>

            {selected && (
              <section className="sticky top-6 flex max-w-[420px] flex-1 basis-[320px] flex-col gap-3.5 rounded-cf-lg border border-cf-line bg-cf-surface px-[22px] py-5">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase">
                    {selected.documentType}
                  </p>
                  <h2 className="mt-1 text-[18px] font-semibold tracking-[-0.02em]">
                    {documentWho(selected)}
                  </h2>
                </div>

                <DocumentPreview document={selected} onView={handleView} />
                <DocumentChecks document={selected} />

                <textarea
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  placeholder={t('verification.admin.docs.commentPlaceholder')}
                  className="min-h-16 w-full resize-y rounded-cf-md border border-cf-line px-3 py-2.5 text-[13px]"
                />

                <DecisionButtons
                  status={statusOf(selected)}
                  isBusy={isBusy}
                  onApprove={() => selected._id && approveMutation.mutate(selected._id)}
                  onReject={() => openRejectFor(selected)}
                />
              </section>
            )}
          </div>
        </>
      )}

      {rejectTarget && (
        <RejectModal
          reason={comment}
          isBusy={rejectMutation.isPending}
          onReasonChange={setComment}
          onCancel={() => setRejectTarget(null)}
          onConfirm={confirmReject}
        />
      )}
    </>
  );
}

/** One row of the desktop queue: who, what, status, how long they have waited. */
function QueueRow({
  document,
  active,
  onPick,
}: {
  document: DocumentWithUser;
  active: boolean;
  onPick: () => void;
}) {
  const status = document.verificationStatus ?? DocumentVerificationStatus.PENDING;
  const tone = statusTone(status);

  return (
    <button
      type="button"
      onClick={onPick}
      className={`flex w-full flex-wrap items-center gap-x-3.5 gap-y-2 border-b border-cf-line-soft px-[18px] py-3 text-left hover:bg-cf-surface-sunken ${tone.stripe} ${
        active ? 'bg-cf-surface-sunken' : 'bg-cf-surface'
      }`}
    >
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold">{documentWho(document)}</div>
        <div className="text-xs text-cf-ink-subtle">
          {document.documentType} · {documentFile(document)}
        </div>
      </div>
      <StatusChip status={status} />
      <span className="font-mono text-xs text-cf-ink-subtle">{documentWhen(document)}</span>
    </button>
  );
}

/**
 * «У цьому статусі документів немає» — the artboard's own words, over the
 * illustration it pairs them with (90px on the phone, 110px on the desktop).
 *
 * A healthy KYC queue is empty most of the day, so this is the state the
 * operator sees most often: it is drawn, not left to a bare sentence.
 */
function EmptyState() {
  return (
    <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-7 text-center lg:px-6 lg:py-10">
      <img
        src="/assets/cf/illu/dokument_pechatka.png"
        alt=""
        className="mx-auto mb-2.5 block h-[90px] w-auto lg:mb-3 lg:h-[110px]"
      />
      <p className="text-base font-semibold lg:text-[18px]">
        {t('verification.admin.docs.empty')}
      </p>
    </section>
  );
}

function Notice({ text }: { text: string }) {
  return (
    <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-7 text-center text-[13px] text-cf-ink-subtle lg:px-6 lg:py-10">
      {text}
    </section>
  );
}
